import { PropsWithChildren } from 'react';
import { ConditionalRender, TrialProgramImage } from '@ui';
import { useInView } from 'react-intersection-observer';
import classnames from 'classnames';
import { useDisclosure } from '@ds';
import { CaseCardType } from '@lp/features/CasesBoard/components/CaseCard/constants';
import { BaseCaseInfo } from '@lp/features/CasesBoard/components/CaseCard/components/BaseCaseInfo/BaseCaseInfo';
import { CardLayout } from '@features/components/CaseCard/Blocks/Base/CardLayout';
import { ResourcesList } from '@features/components/CaseCard/Blocks/Resources/ResourcesList';
import { ResourcesFooter } from '@features/components/CaseCard/Blocks/Resources/ResourcesFooter';
import { LogisticsStatus } from '@features/components/CaseCard/Blocks/Quote/LogisticsStatus';
import { CaseOutcome } from '@features/components/CaseCard/Blocks/Base/CaseOutcome';
import { CaseScsTransplant } from '@features/components/CaseCard/Blocks/Base/CaseScsTransplant';
import { getCardTitleDateTime, getLogisticsStatuses } from '@features/components/CaseCard/utils';
import { logisticsStatuses, scsStatuses, serviceOutcomeStatuses } from '@features/components/CaseCard/constants';
import { useCaseDuplicatesList } from '@lp/features/CaseDuplicatesList/hooks/useCaseDuplicatesList';
import { Case } from '@/api/apis/case';
import { useDeviceTrackingByCaseIdQuery } from '@/queries/device-tracking';
import { getColorsByOrganType, getUnosId, isRandomizationScs } from '@/utils/helpers';
import { CaseCardDuplicates } from '../Duplicates/CaseCardDuplicates';
import { getCardHeight } from './utils';

export type BaseCaseCardProps = {
  cardType?: CaseCardType;
  caseData: Case;
  selectType?: 'single' | 'multiple';
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  extraHeight?: number;
  onDuplicateClick?: (duplicate: Case) => void;
};

export const BaseCaseCard = ({
  cardType = CaseCardType.Base,
  extraHeight = 0,
  selectType = 'single',
  isSelected = false,
  isDisabled = false,
  caseData,
  children,
  onClick,
  onDuplicateClick,
}: BaseCaseCardProps & PropsWithChildren) => {
  const isCandidateCard = cardType === CaseCardType.Candidate;
  const {
    status,
    organType,
    donorId,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    resources,
    assignedEmployees = {},
    caseId,
    trialProgram,
    randomization,
    laterality,
    duplicateCaseNumber,
  } = caseData;
  const { ref, inView } = useInView({ triggerOnce: true });
  const { isOpen: isResourceView, onClose: closeResources, onOpen: openResources } = useDisclosure();
  const { data: deviceTracking } = useDeviceTrackingByCaseIdQuery(caseId, false);
  const { relatedDuplicates } = useCaseDuplicatesList({
    enabled: !isCandidateCard,
    relatedToCaseId: caseId,
    donorId,
    organType,
  });
  const colors = getColorsByOrganType(organType);
  const { label: dateLabel, value: dateValue } = getCardTitleDateTime(caseData, deviceTracking);
  const unosId = getUnosId(
    donorId,
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  );
  const { quoteStatus, routeStatus } = getLogisticsStatuses(caseData);
  const showLogistics = logisticsStatuses.includes(status);
  const isScs = isRandomizationScs(randomization);
  const showScs = !isCandidateCard && scsStatuses.includes(caseData.status) && isScs;
  const showOutcome = !isCandidateCard && serviceOutcomeStatuses.includes(status) && !isScs;
  const layoutHeight = getCardHeight(caseData, extraHeight);
  const emptyCardLayoutStyles = isResourceView || !inView ? { height: layoutHeight } : {};

  const handleResourcesOpen = () => {
    if (isCandidateCard) return;

    openResources();
  };

  return (
    <div className="scroll scroll-sm" ref={ref}>
      <CardLayout
        style={emptyCardLayoutStyles}
        color={colors.bg70}
        isSelected={isSelected}
        isDisabled={isDisabled}
        isCandidateCard={isCandidateCard}
        organType={organType}
        onClick={onClick}
      >
        <ConditionalRender show={inView || isCandidateCard}>
          {isResourceView && resources ? (
            <ResourcesList
              donorId={organType ? unosId : ''}
              onFlip={closeResources}
              organType={organType}
              resources={resources}
              assignedEmployees={assignedEmployees}
              isScs={isScs}
            />
          ) : (
            <>
              <BaseCaseInfo
                cardType={cardType}
                color={colors.bg50}
                dateLabel={dateLabel}
                dateValue={dateValue}
                openResources={handleResourcesOpen}
                caseData={caseData}
                selectType={selectType}
              />
              {showLogistics && <LogisticsStatus routeStatus={routeStatus} quoteStatus={quoteStatus} />}
              {showOutcome && <CaseOutcome caseData={caseData} />}
              {showScs && <CaseScsTransplant color={colors.bg80} />}
              {children}
              <div
                className={classnames('h-6 flex justify-between items-center w-full pl-2', {
                  'mt-auto': isCandidateCard,
                })}
              >
                <ResourcesFooter
                  onClick={handleResourcesOpen}
                  resources={resources}
                  assignedEmployees={assignedEmployees}
                />
                <div className="flex justify-end items-center gap-1">
                  {!isCandidateCard && (
                    <CaseCardDuplicates duplicates={relatedDuplicates} onDuplicateClick={onDuplicateClick} />
                  )}
                  <TrialProgramImage organ={trialProgram?.organ} width={77} height={24} />
                </div>
              </div>
            </>
          )}
        </ConditionalRender>
      </CardLayout>
    </div>
  );
};
